



import React, { useState, useRef,useEffect } from 'react';
import { Modal, Typography, Divider, Row, Col, Tag, Button, Card, message } from 'antd';
import { 
  Calendar, 
  User, 
  DollarSign, 
  Shield, 
  Mail, 
  ClipboardCheck, 
  Bookmark,
  X,
  Download
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const { Title, Text } = Typography;

// Brand colors
const PRIMARY_COLOR = '#005c61'; // Teal/dark cyan
const SECONDARY_COLOR = '#ff601c'; // Vibrant orange

const PolicyDetailsModal = ({isModalOpen,setIsModalOpen}) => {
  // State to control the modal visibility
 
  // State to track loading state for download button
  const [isDownloading, setIsDownloading] = useState(false);
  // Reference to the policy content div for PDF generation
  const policyContentRef = useRef(null);

  const [policyData,setPolicyData]=useState({});

  // Sample policy data from the API response
  const policyData1 = {
    account_id: "67f4b0ae90d18f582492d302",
    currency: "Kes",
    name: "firstName",
    plan_name: "Plan Name",
    policy_end_date: "2026-04-24",
    policy_number: "20250424-12-01-8789",
    policy_start_date: "2025-04-24",
    premium_amount: 430333.22304,
    sponsor_email: "gaurav@edencaremedical.com"
  };
  
  useEffect(()=>{
    const data=sessionStorage.getItem("policy") && JSON.parse(sessionStorage.getItem("policy"));
    if(data){
        setPolicyData(data);
    }else {
        setPolicyData(policyData1)
    }
  },[])

  // Function to format date to a more readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Function to format the currency
  const formatCurrency = (amount, currency) => {
    if(currency)
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'Kes' ? 'KES' : currency,
      minimumFractionDigits: 2
    }).format(amount);
    else {
        return amount
    }
  };

  // Function to generate and download PDF using html2canvas and jsPDF
  const handleDownloadPDF = async () => {
    if (!policyContentRef.current) return;
    
    try {
      setIsDownloading(true);
      
      // Create canvas from the policy content
      const canvas = await html2canvas(policyContentRef.current, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Convert canvas to PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm (portrait)
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // Add footer
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Eden Care Medical - Generated on ${new Date().toLocaleDateString()}`, 105, pageHeight - 10, { align: 'center' });
      
      // Save the PDF
      pdf.save(`Policy_${policyData.policy_number}.pdf`);
      
      message.success('Policy document downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to download policy document');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {/* Button to open the modal */}
   

      {/* Policy Details Modal */}
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
        closeIcon={<X size={20} />}
        centered
      >
        <Card 
          bordered={false} 
          style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div ref={policyContentRef}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginBottom: '20px',
              background: `linear-gradient(45deg, ${PRIMARY_COLOR}, ${PRIMARY_COLOR}DD)`,
              padding: '16px',
              borderRadius: '8px',
              color: 'white'
            }}>
              <Shield size={36} style={{ marginRight: '12px' }} />
              <Title level={3} style={{ margin: 0, color: 'white' }}>
                Policy Confirmation
              </Title>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <Row gutter={[16, 24]} style={{ padding: '0 8px' }}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <ClipboardCheck size={20} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong style={{ fontSize: '16px' }}>Policy Number:</Text>
                </div>
                <Tag color={PRIMARY_COLOR} style={{ marginTop: '4px', padding: '4px 8px', fontSize: '14px' }}>
                  {policyData.policy_number}
                </Tag>
              </Col>

              <Col span={12}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <User size={18} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong>Policyholder:</Text>
                </div>
                <Text style={{ display: 'block', marginTop: '4px' }}>{policyData.name}</Text>
              </Col>

              <Col span={12}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Bookmark size={18} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong>Plan Name:</Text>
                </div>
                <Text style={{ display: 'block', marginTop: '4px' }}>{policyData.plan_name}</Text>
              </Col>

              <Col span={12}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Calendar size={18} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong>Start Date:</Text>
                </div>
                <Text style={{ display: 'block', marginTop: '4px' }}>{formatDate(policyData.policy_start_date)}</Text>
              </Col>

              <Col span={12}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Calendar size={18} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong>End Date:</Text>
                </div>
                <Text style={{ display: 'block', marginTop: '4px' }}>{formatDate(policyData.policy_end_date)}</Text>
              </Col>

              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <DollarSign size={18} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong>Premium Amount:</Text>
                </div>
                <Text style={{ display: 'block', marginTop: '4px', fontSize: '16px', fontWeight: 'bold', color: SECONDARY_COLOR }}>
                  {formatCurrency(policyData.premium_amount, policyData.currency)}
                </Text>
              </Col>

              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Mail size={18} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong>Sponsor Email:</Text>
                </div>
                <Text style={{ display: 'block', marginTop: '4px' }}>{policyData.sponsor_email}</Text>
              </Col>

              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Shield size={18} style={{ marginRight: '8px', color: PRIMARY_COLOR }} />
                  <Text strong>Account ID:</Text>
                </div>
                <Text style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#888' }}>{policyData.account_id}</Text>
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
            <Button 
              onClick={() => setIsModalOpen(false)}
              style={{ borderRadius: '6px' }}
            >
              Close
            </Button>
            <Button 
              type="primary"
              onClick={handleDownloadPDF}
              loading={isDownloading}
              icon={<Download size={16} style={{ marginRight: '4px' }} />}
              style={{ 
                borderRadius: '6px',
                background: SECONDARY_COLOR,
                border: 'none'
              }}
            >
              Download Policy Confirmation
            </Button>
          </div>
        </Card>
      </Modal>
    </>
  );
};

export default PolicyDetailsModal;